// Apolo IDE — preview viewport (sprint code-preview-engine v3).
//
// In the packaged Electron app the visible host is the shared BrowserView (a real
// browser surface: loads ANY url incl. sites that block <iframe> via
// X-Frame-Options, and powers CDP devtools). This component is the SINGLE OWNER of
// the view: it attaches (load + position) it to this host's rect and detaches on
// unmount. Outside Electron it falls back to a sandboxed <iframe> (localhost only;
// external sites show an "open in a window" notice).

import { useEffect, useRef, useState } from 'react';
import { LoaderCircle, MonitorPlay, AlertTriangle, ExternalLink } from 'lucide-react';
import { getDevice } from './DevicePresets.js';
import {
  isLocalPreviewUrl,
  isPreviewCdp,
  previewAttach,
  previewBounds,
  previewDetach,
} from '../../../lib/previewClient.js';

function EmptyState({ hostRef, state }) {
  return (
    <div
      ref={hostRef}
      className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-center"
      data-testid="preview-viewport-empty"
    >
      {state === 'starting' ? (
        <>
          <LoaderCircle size={22} className="animate-spin text-apolo-textTertiary" aria-hidden="true" />
          <p className="text-sm text-apolo-textSecondary">Subindo o dev server…</p>
        </>
      ) : state === 'crashed' ? (
        <>
          <AlertTriangle size={22} className="text-apolo-error" aria-hidden="true" />
          <p className="max-w-[240px] text-sm text-apolo-textSecondary">
            O dev server subiu e caiu. Veja o log ou reinicie.
          </p>
          <p className="max-w-[240px] text-[11px] text-apolo-textTertiary">
            Ou cole a URL de um servidor já rodando (ex.: localhost:5173).
          </p>
        </>
      ) : state === 'no_script' ? (
        <>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-apolo-borderSubtle bg-apolo-surface2/60 text-apolo-textTertiary">
            <MonitorPlay size={22} aria-hidden="true" />
          </span>
          <p className="max-w-[240px] text-sm text-apolo-textSecondary">
            Nenhum dev server web detectado neste projeto.
          </p>
          <p className="max-w-[240px] text-[11px] text-apolo-textTertiary">
            Cole a URL do seu dev server no campo acima (ex.: localhost:5173).
          </p>
        </>
      ) : (
        <>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-dashed border-apolo-borderSubtle bg-apolo-surface2/60 text-apolo-textTertiary">
            <MonitorPlay size={22} aria-hidden="true" />
          </span>
          <p className="max-w-[240px] text-sm text-apolo-textSecondary">
            Inicie o dev server, ou cole uma URL acima, para ver a página ao vivo aqui.
          </p>
        </>
      )}
    </div>
  );
}

function ExternalSiteNotice({ url }) {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-center"
      data-testid="preview-external-notice"
    >
      <AlertTriangle size={22} className="text-apolo-warning" aria-hidden="true" />
      <p className="max-w-[240px] text-sm text-apolo-textSecondary">
        Sites externos não podem ser embutidos no preview (eles bloqueiam iframe).
      </p>
      <button
        type="button"
        onClick={() => window.open(url, '_blank', 'noopener')}
        className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent/10 px-3 py-1.5 text-[12px] font-medium text-apolo-accent transition hover:bg-apolo-accent/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
      >
        <ExternalLink size={13} aria-hidden="true" />
        Abrir em nova janela
      </button>
      <p className="max-w-[240px] text-[11px] text-apolo-textTertiary">
        O preview embutido funciona com dev servers locais (localhost / 127.0.0.1).
      </p>
    </div>
  );
}

export default function PreviewViewport({ url, device = 'desktop', state = 'idle', paused = false }) {
  const hostRef = useRef(null);
  const [hostWidth, setHostWidth] = useState(328);
  const cdp = isPreviewCdp();

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setHostWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Electron: SINGLE OWNER of the BrowserView. attach() loads (only when the URL
  // changes) + positions; repeated calls just re-sync position. ResizeObserver
  // misses pure-position moves so we also poll. Detach removes it on unmount.
  useEffect(() => {
    if (!cdp || !url) return undefined;
    // While the rail is being drag-resized, park the native view off-screen and
    // stop syncing so window pointermove keeps reaching the resize handle.
    if (paused) {
      previewBounds({ bounds: { x: -100000, y: 0, width: 0, height: 0 } });
      return undefined;
    }
    const el = hostRef.current;
    if (!el) return undefined;
    const rect = () => {
      const r = el.getBoundingClientRect();
      // Inset the native view from the LEFT so the resize handle's gutter is never
      // painted over (a BrowserView always paints above DOM and eats its clicks).
      const inset = 12;
      const w = r.width - inset;
      return w > 0 && r.height > 0
        ? { x: r.left + inset, y: r.top, width: w, height: r.height }
        : null;
    };
    // attach (load + position) ONCE per URL; the interval below only repositions
    // (lightweight setBounds), never re-runs the CDP setup — re-running attach on
    // every tick stacked concurrent CDP calls that hung the load.
    const r0 = rect();
    if (r0) previewAttach({ url, bounds: r0, vw: window.innerWidth, vh: window.innerHeight });
    const sync = () => {
      const r = rect();
      if (r) previewBounds({ bounds: r, vw: window.innerWidth, vh: window.innerHeight });
    };
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    // Onda 1 (anti-travamento): o tick de 400ms só precisa rodar quando a janela
    // está VISÍVEL — ele é um belt-and-suspenders de reposição; o ResizeObserver +
    // scroll + resize já pegam qualquer mudança real de bounds. Com a janela oculta
    // (minimizada/atrás) e backgroundThrottling:false, esse tick queimava CPU à toa.
    // Pausa quando oculto; ao reaparecer, sincroniza na hora e retoma o tick.
    let iv = null;
    const startTick = () => { if (!iv) iv = setInterval(sync, 400); };
    const stopTick = () => { if (iv) { clearInterval(iv); iv = null; } };
    const onVisibility = () => {
      if (document.hidden) stopTick();
      else { sync(); startTick(); }
    };
    if (!document.hidden) startTick();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
      document.removeEventListener('visibilitychange', onVisibility);
      stopTick();
      previewDetach();
    };
  }, [cdp, url, paused]);

  if (!url) {
    return <EmptyState hostRef={hostRef} state={state} />;
  }

  // Electron: empty host — the native BrowserView overlays this rect.
  if (cdp) {
    return (
      <div ref={hostRef} className="relative min-h-0 flex-1 bg-apolo-bg" data-testid="preview-viewport">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] text-apolo-textTertiary">
          Carregando preview…
        </div>
      </div>
    );
  }

  // Web fallback: external sites can't be iframed.
  if (!isLocalPreviewUrl(url)) {
    return <ExternalSiteNotice url={url} />;
  }

  const preset = getDevice(device);
  const isResponsive = device === 'responsive' || !preset.width;

  if (isResponsive) {
    return (
      <div ref={hostRef} className="min-h-0 flex-1 bg-apolo-bg" data-testid="preview-viewport">
        <iframe
          title="Preview ao vivo"
          src={url}
          className="h-full w-full border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    );
  }

  const scale = Math.min(1, (hostWidth - 16) / preset.width);
  const scaledW = Math.round(preset.width * scale);
  const scaledH = Math.round(preset.height * scale);

  return (
    <div
      ref={hostRef}
      className="flex min-h-0 flex-1 items-start justify-center overflow-auto bg-apolo-bg p-2"
      data-testid="preview-viewport"
    >
      <div style={{ width: scaledW, height: scaledH }} className="shrink-0 overflow-hidden rounded-lg border border-apolo-border shadow-lg">
        <iframe
          title={`Preview ${preset.label}`}
          src={url}
          style={{
            width: preset.width,
            height: preset.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
          className="border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  );
}
